import { useEffect, useId, useRef, useState } from "react";
import { TextField } from "@/components/TextField";
import {
  asText,
  type BondFieldValue,
  type BondRenderField,
} from "@/lib/bondFields";
import { formatSsn, isBlankPiiValue, maskedSsn } from "@/lib/personnelPii";

// BRK-S316 — an SSN that is ON FILE, drawn masked to its last four.
//
// Daniel's walk (week of 10/4): returning to the indemnitor step showed an
// EMPTY SSN box, which reads as "we lost it" and invites a retype — or a save
// the broker fears will wipe it. So a stored SSN is a read-only `•••-••-1234`
// with two affordances:
//
//   • an EYE, only when this session still HOLDS what the broker typed. PMS
//     never sends the plaintext back (BRK-S105), so a value it holds that the
//     page does not can be masked and changed, never revealed;
//   • CHANGE, which opens the ordinary input — the gate comes back with it, so
//     clearing a stored SSN is refused rather than read as "keep it". "Keep the
//     SSN on file" closes the input again and drops whatever was typed.
//
// Untouched, the field carries no value, so the save sends no `ssn` entry for
// this person and PMS preserves what it holds (the PMS-S184 shape).

/** What the masked control needs from the page, which owns both facts: the
 *  value a save accepted this session (memory only, NN #9), and whether the
 *  broker has asked to replace the stored one — the page's gate reads the
 *  latter too, which is why it is not this component's local state. */
export interface BondPiiPort {
  held: (name: string) => string | undefined;
  changing: (name: string) => boolean;
  setChanging: (name: string, changing: boolean) => void;
}

/** What the box shows for a legacy row PMS stored no last four for — masked
 *  all the way through rather than given invented digits. */
const NO_LAST4 = "••••";

export function BondSsnField({
  field,
  storedSsn,
  value,
  onChange,
  error,
  port,
}: {
  field: BondRenderField;
  storedSsn: { last4: string | null };
  value: BondFieldValue | undefined;
  onChange: (value: BondFieldValue | undefined) => void;
  error?: string;
  port: BondPiiPort;
}) {
  const [revealed, setRevealed] = useState(false);
  const inputId = useId();
  // Focus follows Change into the input it opened — a keyboard user pressed a
  // button that vanished, and leaving focus on nothing loses their place.
  const focusInput = useRef(false);
  const editing = port.changing(field.name) || !isBlankPiiValue(value);

  useEffect(() => {
    if (editing && focusInput.current) {
      focusInput.current = false;
      document.getElementById(inputId)?.focus();
    }
  }, [editing, inputId]);

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        <TextField
          key="edit"
          id={inputId}
          label={field.label}
          value={asText(value)}
          onChange={(e) =>
            onChange(e.target.value === "" ? undefined : e.target.value)
          }
          required={field.required}
          inputMode="numeric"
          autoComplete="off"
          error={error}
        />
        <button
          type="button"
          onClick={() => {
            onChange(undefined);
            port.setChanging(field.name, false);
          }}
          className="self-start text-xs font-medium text-brk-accent hover:underline"
        >
          Keep the {field.label} on file
        </button>
      </div>
    );
  }

  const held = port.held(field.name);
  const shown =
    revealed && held ? formatSsn(held) : maskedSsn(storedSsn.last4 ?? NO_LAST4);
  return (
    <div className="flex flex-col gap-1">
      <TextField
        key="masked"
        id={inputId}
        label={field.label}
        value={shown}
        readOnly
        error={error}
        endControl={
          held ? (
            <button
              type="button"
              aria-label={`${revealed ? "Hide" : "Show"} ${field.label}`}
              aria-pressed={revealed}
              onClick={() => setRevealed((on) => !on)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brk-dim hover:text-brk-text"
            >
              <EyeIcon crossed={revealed} />
            </button>
          ) : undefined
        }
      />
      <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
        <span className="text-brk-dim">
          {field.label} on file — kept unless you change it.
        </span>
        <button
          type="button"
          aria-label={`Change ${field.label}`}
          onClick={() => {
            focusInput.current = true;
            setRevealed(false);
            port.setChanging(field.name, true);
          }}
          className="font-medium text-brk-accent hover:underline"
        >
          Change
        </button>
      </div>
    </div>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
      {crossed && (
        <path
          d="M4 4l16 16"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}
